import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiClient } from '@/lib/api';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Vessel {
  mmsi: string;
  imo?: string;
  name: string;
  type: string;
  flag: string;
  latitude: number;
  longitude: number;
  speed: number;
  course: number;
  destination?: string;
  eta?: string;
  status: string;
  image_url?: string;
  vessel_image?: string;
  gross_tonnage?: number;
  deadweight?: number;
  year_built?: number;
  builder?: string;
  owner?: string;
  port_of_registry?: string;
  current_port?: string;
  previous_port?: string;
  next_port?: string;
  distance_traveled?: number;
  time_in_port?: string;
  length?: number;
  width?: number;
  draught?: number;
}

interface CustomMarker {
  id: string;
  type: 'vessel' | 'harbor' | 'coordinate' | 'area';
  name: string;
  latitude: number;
  longitude: number;
  description?: string;
  color?: string;
}

// Component to update map view when center/zoom changes
function MapViewUpdater({ center, zoom }: { center: [number, number], zoom: number }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] !== 0 && center[1] !== 0) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  
  return null;
}

// Component to fetch and display vessel image on demand
function VesselImage({ vessel }: { vessel: Vessel }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  useEffect(() => {
    const fetchImage = async () => {
      if (!vessel.vessel_image && !vessel.image_url) {
        setLoading(false);
        setError(true);
        return;
      }
      
      try {
        const url = vessel.vessel_image || vessel.image_url || '';
        let vesselId: string | null = null;
        const vesselIdMatch = url.match(/vessel-image\/(\d+)/) || url.match(/asset_id=(\d+)/) || url.match(/shipid[:\/](\d+)/);
        
        if (vesselIdMatch) {
          vesselId = vesselIdMatch[1];
        } else if (vessel.imo) {
          vesselId = vessel.imo;
        }
        
        if (vesselId) {
          const imageResponse = await fetch(`/api/maritime/vessel-image/${vesselId}`);
          
          if (imageResponse.ok) {
            const blob = await imageResponse.blob();
            const imageUrl = URL.createObjectURL(blob);
            setImageUrl(imageUrl);
            setError(false);
          } else {
            if (url && !url.includes('placeholder')) {
              setImageUrl(url);
            } else {
              setError(true);
            }
          }
        } else if (url && !url.includes('placeholder')) {
          setImageUrl(url);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to fetch vessel image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchImage();
    
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [vessel.vessel_image, vessel.image_url, vessel.mmsi]);
  
  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800">
        <div className="text-blue-600 dark:text-blue-300 text-sm">Loading image...</div>
      </div>
    );
  }
  
  if (error || !imageUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600">
        <div className="text-center text-white">
          <div className="text-4xl mb-2">🚢</div>
          <div className="text-sm font-semibold">{vessel.name || 'Vessel'}</div>
        </div>
      </div>
    );
  }
  
  return (
    <img 
      src={imageUrl} 
      alt={vessel.name || 'Vessel'}
      className="w-full h-full object-cover"
      onError={() => {
        setError(true);
        if (imageUrl && imageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(imageUrl);
        }
      }}
    />
  );
}

const GEOINT_Maritime: React.FC = () => {
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const [mapZoom, setMapZoom] = useState(3);
  const [vesselConfig, setVesselConfig] = useState({
    latitude: "0.00",
    longitude: "0.00",
    zoom: "4",
  });
  const [customMarkers, setCustomMarkers] = useState<CustomMarker[]>([]);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
  const [showVesselDialog, setShowVesselDialog] = useState(false);
  const [detectedPort, setDetectedPort] = useState<{ name: string; lat: number; lon: number } | null>(null);
  
  // Monitoring state
  const [monitoredPorts, setMonitoredPorts] = useState<Array<{ id: string; name: string; lat: number; lon: number; vessels: Vessel[]; lastUpdate: Date }>>([]);
  const [monitoredVessels, setMonitoredVessels] = useState<Array<{ id: string; mmsi: string; name: string; lastPosition: { lat: number; lon: number }; lastDestination: string; alerts: string[] }>>([]);
  const [monitoringActive, setMonitoringActive] = useState(false);
  const monitoringIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Vessel search (by MMSI, IMO, or name)
  const [vesselSearchQuery, setVesselSearchQuery] = useState('');
  const [vesselSearchResults, setVesselSearchResults] = useState<Vessel[]>([]);
  const [vesselSearchLoading, setVesselSearchLoading] = useState(false);
  const [vesselSearchSource, setVesselSearchSource] = useState<string | null>(null);

  // Create custom vessel icon with direction arrow and vessel type indicator
  const createVesselIcon = useCallback((vessel: Vessel) => {
    const course = vessel.course || 0;
    const speed = vessel.speed || 0;
    const isMoving = vessel.status === 'Underway' && speed > 0;
    const isStationary = speed === 0 || vessel.status === 'Moored' || vessel.status === 'At Anchor';
    
    const vesselTypeStr = (vessel.type || '').toLowerCase();
    let vesselColor = '#3b82f6';
    let vesselShape = 'ship'; // default ship shape
    
    if (vesselTypeStr.includes('tanker') || vesselTypeStr.includes('oil')) {
      vesselColor = '#ef4444';
      vesselShape = 'tanker';
    } else if (vesselTypeStr.includes('cargo') && !vesselTypeStr.includes('container')) {
      vesselColor = '#10b981';
      vesselShape = 'cargo';
    } else if (vesselTypeStr.includes('container')) {
      vesselColor = '#06b6d4';
      vesselShape = 'container';
    } else if (vesselTypeStr.includes('fishing')) {
      vesselColor = '#f59e0b';
      vesselShape = 'fishing';
    } else if (vesselTypeStr.includes('passenger') || vesselTypeStr.includes('ferry') || vesselTypeStr.includes('cruise')) {
      vesselColor = '#8b5cf6';
      vesselShape = 'passenger';
    } else if (vesselTypeStr.includes('tug') || vesselTypeStr.includes('towing')) {
      vesselColor = '#f97316';
      vesselShape = 'tug';
    } else if (vesselTypeStr.includes('yacht') || vesselTypeStr.includes('pleasure')) {
      vesselColor = '#ec4899';
      vesselShape = 'yacht';
    }
    
    const iconSize = isStationary ? 32 : (isMoving ? Math.min(56, 36 + speed * 0.6) : 36);
    
    return L.divIcon({
      className: 'vessel-marker',
      html: `
        <div class="vessel-icon-container" style="transform: rotate(${course}deg)">
          <div class="vessel-icon ${isMoving ? 'moving' : isStationary ? 'stationary-harbor' : 'stationary'} vessel-${vesselShape}" style="--vessel-color: ${vesselColor}; --icon-size: ${iconSize}px;">
            <div class="vessel-hull"></div>
            <div class="vessel-superstructure"></div>
            <div class="vessel-bow"></div>
            ${isMoving ? '<div class="vessel-wake"></div>' : ''}
            ${isStationary ? '<div class="harbor-anchor"></div>' : ''}
            <div class="vessel-type-badge">${vesselShape.charAt(0).toUpperCase()}</div>
          </div>
        </div>
      `,
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconSize / 2, iconSize / 2]
    });
  }, []);

  // Create port/harbor icon
  const createPortIcon = useCallback((portName: string) => {
    return L.divIcon({
      className: 'port-marker',
      html: `
        <div class="port-icon-container">
          <div class="port-icon">
            <div class="port-base"></div>
            <div class="port-flag">⚓</div>
            <div class="port-label">${portName}</div>
          </div>
        </div>
      `,
      iconSize: [60, 60],
      iconAnchor: [30, 30]
    });
  }, []);

  // Get route from previous port to current position
  const getVesselRoute = useCallback((vessel: Vessel): [number, number][] | null => {
    if (!vessel.latitude || !vessel.longitude) {
      return null;
    }
    
    const currentPos: [number, number] = [vessel.latitude, vessel.longitude];
    
    if (vessel.previous_port) {
      const portCoords: Record<string, [number, number]> = {
        'Rotterdam': [51.9225, 4.4803],
        'Singapore': [1.2897, 103.8501],
        'Los Angeles': [33.7490, -118.2648],
        'Hamburg': [53.5511, 9.9937],
        'Shanghai': [31.2304, 121.4737],
        'Dubai': [25.2048, 55.2708],
        'Mumbai': [19.0760, 72.8777],
        'Kochi': [9.9312, 76.2673],
        'Mangalore': [12.9141, 74.8560],
        'Chennai': [13.0827, 80.2707],
        'Kolkata': [22.5726, 88.3639],
        'Karachi': [24.8547, 67.0207],
        'Port Qasim': [24.7667, 67.3333],
        'Gwadar': [25.1264, 62.3225],
      };
      
      const portName = vessel.previous_port.toLowerCase();
      for (const [name, coords] of Object.entries(portCoords)) {
        if (portName.includes(name.toLowerCase())) {
          return [coords, currentPos];
        }
      }
      
      if (vessel.course && vessel.distance_traveled) {
        const distanceNm = typeof vessel.distance_traveled === 'number' ? vessel.distance_traveled : 0;
        const distanceKm = distanceNm * 1.852;
        const distanceDeg = distanceKm / 111;
        
        const courseRad = ((vessel.course + 180) % 360) * Math.PI / 180;
        const latOffset = -distanceDeg * Math.cos(courseRad);
        const lonOffset = distanceDeg * Math.sin(courseRad) / Math.cos(vessel.latitude * Math.PI / 180);
        
        const prevLat = vessel.latitude + latOffset;
        const prevLon = vessel.longitude + lonOffset;
        
        return [[prevLat, prevLon], currentPos];
      }
    }
    
    if (vessel.course && vessel.speed && vessel.speed > 0) {
      const path: [number, number][] = [];
      const numPoints = 20;
      const hoursPerPoint = 0.1;
      
      for (let i = numPoints; i >= 0; i--) {
        const hoursAgo = i * hoursPerPoint;
        const distanceNm = vessel.speed * hoursAgo;
        const distanceKm = distanceNm * 1.852;
        const distanceDeg = distanceKm / 111;
        
        const courseRad = (vessel.course * Math.PI) / 180;
        const latOffset = -distanceDeg * Math.cos(courseRad);
        const lonOffset = distanceDeg * Math.sin(courseRad) / Math.cos(vessel.latitude * Math.PI / 180);
        
        const pathLat = vessel.latitude + latOffset;
        const pathLon = vessel.longitude + lonOffset;
        
        path.push([pathLat, pathLon]);
      }
      
      return path;
    }
    
    return null;
  }, []);

  // Fetch vessels from API (area + optional monitored MMSIs)
  const fetchVessels = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const lat = parseFloat(vesselConfig.latitude) || 0;
      const lon = parseFloat(vesselConfig.longitude) || 0;
      const searchRadius = 0.5;

      const request: any = {
        south: lat - searchRadius,
        west: lon - searchRadius,
        north: lat + searchRadius,
        east: lon + searchRadius,
        limit: 200,
      };
      if (monitoredVessels.length > 0) {
        request.mmsi_list = monitoredVessels.map((m) => m.mmsi);
      }

      const response = (await apiClient.getMaritimeVessels(request)) as any;
      let vesselData = response.vessels || [];

      if (response.data_source) {
      }

      if (monitoredVessels.length > 0 && vesselData.length < 200) {
        try {
          const monitoredRequest = {
            south: -90,
            west: -180,
            north: 90,
            east: 180,
            limit: 50,
            mmsi_list: monitoredVessels.map((m) => m.mmsi),
          };
          const monitoredRes = (await apiClient.getMaritimeVessels(monitoredRequest)) as any;
          const monitoredList = (monitoredRes.vessels || []).filter(
            (v: Vessel) => v.latitude != null && v.longitude != null
          );
          const byMmsi = new Map(vesselData.map((v: Vessel) => [v.mmsi, v]));
          for (const v of monitoredList) {
            if (!byMmsi.has(v.mmsi)) byMmsi.set(v.mmsi, v);
          }
          vesselData = Array.from(byMmsi.values());
        } catch (_) {
          // ignore; use area results only
        }
      }

      const validVessels = vesselData.filter(
        (v: Vessel) =>
          v.latitude != null &&
          v.longitude != null &&
          !isNaN(v.latitude) &&
          !isNaN(v.longitude)
      );

      setVessels(validVessels);

      if (validVessels.length > 0) {
        const lats = validVessels.map((v: Vessel) => v.latitude as number);
        const lons = validVessels.map((v: Vessel) => v.longitude as number);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);
        setMapCenter([(minLat + maxLat) / 2, (minLon + maxLon) / 2]);
        setMapZoom(parseInt(vesselConfig.zoom) || 10);
      }
    } catch (err: any) {
      console.error('Failed to fetch vessels:', err);
      setError(err.message || 'Failed to fetch vessel data');
    } finally {
      setLoading(false);
    }
  }, [vesselConfig, monitoredVessels]);

  // Start/stop auto-refresh polling when monitoringActive changes
  useEffect(() => {
    if (monitoringActive) {
      monitoringIntervalRef.current = setInterval(() => {
        fetchVessels();
      }, 30_000); // refresh every 30 seconds
    } else {
      if (monitoringIntervalRef.current !== null) {
        clearInterval(monitoringIntervalRef.current);
        monitoringIntervalRef.current = null;
      }
    }
    return () => {
      if (monitoringIntervalRef.current !== null) {
        clearInterval(monitoringIntervalRef.current);
        monitoringIntervalRef.current = null;
      }
    };
  }, [monitoringActive, fetchVessels]);

  const handleVesselSearch = useCallback(async () => {
    const q = vesselSearchQuery.trim();
    if (!q) return;
    setVesselSearchLoading(true);
    setVesselSearchResults([]);
    setVesselSearchSource(null);
    try {
      const res = (await apiClient.getMaritimeSearch(q)) as {
        vessels: Vessel[];
        total_count: number;
        data_source: string;
      };
      setVesselSearchResults(res.vessels || []);
      setVesselSearchSource(res.data_source || null);
      if ((res.vessels || []).length > 0 && res.vessels![0].latitude != null) {
        setMapCenter([res.vessels![0].latitude!, res.vessels![0].longitude!]);
        setMapZoom(12);
      }
    } catch (err: any) {
      console.error('Vessel search failed:', err);
      setError(err.message || 'Vessel search failed');
    } finally {
      setVesselSearchLoading(false);
    }
  }, [vesselSearchQuery]);

  useEffect(() => {
    if (vesselConfig.latitude !== "0.00" || vesselConfig.longitude !== "0.00") {
      fetchVessels();
    }
  }, [fetchVessels]);

  // Update monitored vessels when new vessel data arrives
  useEffect(() => {
    if (vessels.length === 0 || monitoredVessels.length === 0) return;

    setMonitoredVessels(prev => prev.map(monitored => {
      const current = vessels.find(v => v.mmsi === monitored.mmsi);
      if (!current) return monitored;

      const alerts: string[] = [];
      
      // Check for destination change
      if (current.destination && current.destination !== monitored.lastDestination && monitored.lastDestination !== 'Unknown') {
        alerts.push(`Destination changed: ${monitored.lastDestination} → ${current.destination}`);
      }

      // Check for significant position change (>10km) using Haversine
      const dLat = (current.latitude! - monitored.lastPosition.lat) * (Math.PI / 180);
      const dLon = (current.longitude! - monitored.lastPosition.lon) * (Math.PI / 180);
      const midLat = ((current.latitude! + monitored.lastPosition.lat) / 2) * (Math.PI / 180);
      const dist = Math.sqrt(dLat * dLat + Math.pow(dLon * Math.cos(midLat), 2)) * 6371; // km
      
      if (dist > 10 && monitored.lastPosition.lat !== 0) {
        alerts.push(`Position changed: moved ${dist.toFixed(1)} km`);
      }

      return {
        ...monitored,
        lastPosition: { lat: current.latitude!, lon: current.longitude! },
        lastDestination: current.destination || monitored.lastDestination,
        alerts: [...monitored.alerts, ...alerts].slice(-5) // Keep last 5 alerts
      };
    }));
  }, [vessels]);

  // Update monitored ports when new vessel data arrives
  useEffect(() => {
    if (vessels.length === 0 || monitoredPorts.length === 0) return;

    setMonitoredPorts(prev => prev.map(port => {
      // Find vessels within 10km of port
      const portVessels = vessels.filter(v => {
        if (!v.latitude || !v.longitude) return false;
        const dLat = (v.latitude - port.lat) * (Math.PI / 180);
        const dLon = (v.longitude - port.lon) * (Math.PI / 180);
        const midLat = ((v.latitude + port.lat) / 2) * (Math.PI / 180);
        const dist = Math.sqrt(dLat * dLat + Math.pow(dLon * Math.cos(midLat), 2)) * 6371;
        return dist < 10;
      });

      return {
        ...port,
        vessels: portVessels,
        lastUpdate: new Date()
      };
    }));
  }, [vessels]);

  const handleLocationSearch = async (location: string) => {
    if (!location.trim()) return;
    
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&limit=1`);
      const results = await response.json();
      
      if (results.length > 0) {
        const result = results[0];
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        
        // Check if this is a known port city
        const portCoords: Record<string, [number, number]> = {
          'karachi': [24.8547, 67.0207],
          'rotterdam': [51.9225, 4.4803],
          'singapore': [1.2897, 103.8501],
          'los angeles': [33.7490, -118.2648],
          'hamburg': [53.5511, 9.9937],
          'shanghai': [31.2304, 121.4737],
          'dubai': [25.2048, 55.2708],
          'mumbai': [19.0760, 72.8777],
          'kochi': [9.9312, 76.2673],
          'mangalore': [12.9141, 74.8560],
          'chennai': [13.0827, 80.2707],
          'kolkata': [22.5726, 88.3639],
          'port qasim': [24.7667, 67.3333],
          'gwadar': [25.1264, 62.3225],
        };
        
        const locationLower = location.toLowerCase();
        let portFound = null;
        
        for (const [portName, coords] of Object.entries(portCoords)) {
          if (locationLower.includes(portName) || portName.includes(locationLower)) {
            portFound = { name: portName.charAt(0).toUpperCase() + portName.slice(1), lat: coords[0], lon: coords[1] };
            break;
          }
        }
        
        // Use port coordinates if found, otherwise use geocoded coordinates
        const finalLat = portFound ? portFound.lat : lat;
        const finalLon = portFound ? portFound.lon : lon;
        
        setVesselConfig(prev => ({
          ...prev,
          latitude: finalLat.toString(),
          longitude: finalLon.toString(),
          zoom: "11"
        }));
        
        // Set detected port for marker display
        if (portFound) {
          setDetectedPort(portFound);
        } else {
          setDetectedPort(null);
        }
      } else {
        alert('Location not found. Please try a different search term.');
      }
    } catch (error) {
      console.error('Location search failed:', error);
      alert('Location search failed. Please try again.');
    }
  };

  // OSINT Functions - Monitoring
  const handleAddVesselToMonitor = async (vesselData: any) => {
    // Search for vessel by MMSI or IMO
    const identifier = vesselData.mmsi || vesselData.imo || vesselData.name;
    if (!identifier) return;
    
    try {
      // Search in current vessels first
      const foundVessel = vessels.find(v => 
        v.mmsi === identifier || v.imo === identifier || v.name?.toLowerCase().includes(identifier.toLowerCase())
      );
      
      if (foundVessel) {
        const newMonitored = {
          id: `monitor-vessel-${Date.now()}`,
          mmsi: foundVessel.mmsi,
          name: foundVessel.name || 'Unknown',
          lastPosition: { lat: foundVessel.latitude!, lon: foundVessel.longitude! },
          lastDestination: foundVessel.destination || 'Unknown',
          alerts: []
        };
        setMonitoredVessels([...monitoredVessels, newMonitored]);
        alert(`✅ Added ${foundVessel.name || foundVessel.mmsi} to monitoring`);
      } else {
        // If not found, add as pending (will be checked on next refresh)
        const newMonitored = {
          id: `monitor-vessel-${Date.now()}`,
          mmsi: identifier,
          name: vesselData.name || identifier,
          lastPosition: { lat: 0, lon: 0 },
          lastDestination: 'Unknown',
          alerts: []
        };
        setMonitoredVessels([...monitoredVessels, newMonitored]);
        alert(`✅ Added ${identifier} to monitoring (will track when found)`);
      }
    } catch (error) {
      console.error('Error adding vessel to monitor:', error);
      alert('Failed to add vessel to monitoring');
    }
  };

  const handleAddPortToMonitor = async (portData: any) => {
    const portName = portData.name.trim();
    if (!portName) return;
    
    // Search for port in known ports
    const portCoords: Record<string, [number, number]> = {
      'karachi': [24.8547, 67.0207],
      'port qasim': [24.7667, 67.3333],
      'rotterdam': [51.9225, 4.4803],
      'singapore': [1.2897, 103.8501],
      'los angeles': [33.7490, -118.2648],
      'hamburg': [53.5511, 9.9937],
      'shanghai': [31.2304, 121.4737],
      'dubai': [25.2048, 55.2708],
      'mumbai': [19.0760, 72.8777],
      'kochi': [9.9312, 76.2673],
      'mangalore': [12.9141, 74.8560],
      'chennai': [13.0827, 80.2707],
      'kolkata': [22.5726, 88.3639],
      'gwadar': [25.1264, 62.3225],
      'jebel ali': [25.0167, 55.0167],
      'port klang': [3.0000, 101.4000],
      'tanjung pelepas': [1.3667, 103.5500],
    };
    
    const portNameLower = portName.toLowerCase();
    let portFound = null;
    
    for (const [name, coords] of Object.entries(portCoords)) {
      if (portNameLower.includes(name) || name.includes(portNameLower)) {
        portFound = { name: name.charAt(0).toUpperCase() + name.slice(1), lat: coords[0], lon: coords[1] };
        break;
      }
    }
    
    if (!portFound) {
      // Try geocoding
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(portName + ' port')}&limit=1`);
        const results = await response.json();
        if (results.length > 0) {
          portFound = {
            name: portName,
            lat: parseFloat(results[0].lat),
            lon: parseFloat(results[0].lon)
          };
        }
      } catch (error) {
        console.error('Geocoding failed:', error);
      }
    }
    
    if (portFound) {
      const newMonitored = {
        id: `monitor-port-${Date.now()}`,
        name: portFound.name,
        lat: portFound.lat,
        lon: portFound.lon,
        vessels: [],
        lastUpdate: new Date()
      };
      setMonitoredPorts([...monitoredPorts, newMonitored]);
      
      // Fetch vessels at this port
      setVesselConfig(prev => ({
        ...prev,
        latitude: portFound.lat.toString(),
        longitude: portFound.lon.toString(),
        zoom: "11"
      }));
      
      alert(`✅ Added ${portFound.name} to monitoring`);
    } else {
      alert('Port not found. Please check the name or add coordinates manually.');
    }
  };

  const handleAddCoordinate = (coordData: any) => {
    const newMarker: CustomMarker = {
      id: `coord-${Date.now()}`,
      type: 'coordinate',
      name: coordData.name || 'Custom Coordinate',
      latitude: parseFloat(coordData.latitude),
      longitude: parseFloat(coordData.longitude),
      description: coordData.description,
      color: '#f59e0b'
    };
    setCustomMarkers([...customMarkers, newMarker]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4 md:p-6">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                🚢 Maritime Intelligence
              </h1>
              <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base">
                Real-time vessel tracking & OSINT analysis using AIS data
              </p>
            </div>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200">
                {vessels.length} Vessels
              </Badge>
              <Badge variant="outline" className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200">
                {vessels.filter(v => v.status === 'Underway').length} Moving
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Left Sidebar - Controls */}
          <div className="lg:col-span-1 space-y-4">
            <Tabs defaultValue="search" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="search">🔍 Search</TabsTrigger>
                <TabsTrigger value="osint">🎯 OSINT</TabsTrigger>
                <TabsTrigger value="tools">🛠️ Tools</TabsTrigger>
              </TabsList>

              {/* Search Tab */}
              <TabsContent value="search" className="space-y-4">
                <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-blue-600">📍</span> Location Search
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Enter location..."
                        className="flex-1 text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch(searchQuery)}
                      />
                      <Button
                        onClick={() => handleLocationSearch(searchQuery)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        size="sm"
                      >
                        🔍
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-cyan-600">🚢</span> Vessel Search
                    </CardTitle>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      MMSI or IMO (recommended) / vessel name
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={vesselSearchQuery}
                        onChange={(e) => setVesselSearchQuery(e.target.value)}
                        placeholder="e.g. 259000420 or vessel name"
                        className="flex-1 text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && handleVesselSearch()}
                      />
                      <Button
                        onClick={handleVesselSearch}
                        disabled={vesselSearchLoading}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white"
                        size="sm"
                      >
                        {vesselSearchLoading ? '…' : '🔍'}
                      </Button>
                    </div>
                    {vesselSearchSource && (
                      <p className="text-xs text-slate-500">Source: {vesselSearchSource}</p>
                    )}
                    {vesselSearchResults.length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {vesselSearchResults.slice(0, 10).map((v) => (
                          <div
                            key={v.mmsi}
                            className="flex items-center justify-between gap-2 p-2 rounded bg-slate-100 dark:bg-slate-700/50 text-sm"
                          >
                            <div className="min-w-0">
                              <div className="font-medium truncate">{v.name || v.mmsi}</div>
                              <div className="text-xs text-slate-500">
                                MMSI {v.mmsi} {v.latitude != null && `• ${v.latitude.toFixed(4)}, ${v.longitude!.toFixed(4)}`}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="shrink-0"
                              onClick={() => handleAddVesselToMonitor({ mmsi: v.mmsi, name: v.name, imo: v.imo })}
                            >
                              + Monitor
                            </Button>
                          </div>
                        ))}
                        {vesselSearchResults.length > 10 && (
                          <p className="text-xs text-slate-500">+ {vesselSearchResults.length - 10} more</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-purple-600">⚙️</span> Map Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium mb-1 text-slate-600 dark:text-slate-400">Latitude</label>
                        <Input
                          type="number"
                          step="0.0001"
                          value={vesselConfig.latitude}
                          onChange={(e) => setVesselConfig(prev => ({ ...prev, latitude: e.target.value }))}
                          className="w-full text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-slate-600 dark:text-slate-400">Longitude</label>
                        <Input
                          type="number"
                          step="0.0001"
                          value={vesselConfig.longitude}
                          onChange={(e) => setVesselConfig(prev => ({ ...prev, longitude: e.target.value }))}
                          className="w-full text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-slate-600 dark:text-slate-400">Zoom: {vesselConfig.zoom}</label>
                      <input
                        type="range"
                        min="2"
                        max="18"
                        value={vesselConfig.zoom}
                        onChange={(e) => setVesselConfig(prev => ({ ...prev, zoom: e.target.value }))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                    </div>
                    <Button
                      onClick={fetchVessels}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold"
                    >
                      {loading ? '⏳ Loading...' : `🔄 Refresh Vessels (${vessels.length})`}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-green-600">⚡</span> Quick Locations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button
                      onClick={() => {
                        setVesselConfig(prev => ({
                          ...prev,
                          latitude: "1.2966",
                          longitude: "103.8198",
                          zoom: "10"
                        }));
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                      variant="outline"
                    >
                      🌏 Singapore Port
                    </Button>
                    <Button
                      onClick={() => {
                        setVesselConfig(prev => ({
                          ...prev,
                          latitude: "9.9679032",
                          longitude: "76.2444378",
                          zoom: "10"
                        }));
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                      variant="outline"
                    >
                      🗺️ Kochi Port
                    </Button>
                    <Button
                      onClick={() => {
                        setVesselConfig(prev => ({
                          ...prev,
                          latitude: "0.00",
                          longitude: "0.00",
                          zoom: "3"
                        }));
                      }}
                      className="w-full bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white"
                      variant="outline"
                    >
                      🌍 Global View
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* OSINT Tab */}
              <TabsContent value="osint" className="space-y-4">
                <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-red-600">🎯</span> OSINT Monitoring
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white">
                          🚢 Add Vessel to Monitor
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Add Vessel to Monitor</DialogTitle>
                        </DialogHeader>
                        <AddVesselMonitorForm onSubmit={handleAddVesselToMonitor} />
                      </DialogContent>
                    </Dialog>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white">
                          ⚓ Add Port to Monitor
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Add Port to Monitor</DialogTitle>
                        </DialogHeader>
                        <AddPortMonitorForm onSubmit={handleAddPortToMonitor} />
                      </DialogContent>
                    </Dialog>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white">
                          📍 Add Coordinate
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Add Coordinate Marker</DialogTitle>
                        </DialogHeader>
                        <AddCoordinateForm onSubmit={handleAddCoordinate} />
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>

                {/* Monitored Ports */}
                {monitoredPorts.length > 0 && (
                  <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-green-600">⚓</span> Monitored Ports ({monitoredPorts.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                      {monitoredPorts.map((port) => (
                        <div key={port.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded">
                          <div className="flex-1">
                            <div className="text-sm font-medium">{port.name}</div>
                            <div className="text-xs text-slate-500">
                              {port.vessels.length} vessels • Last update: {port.lastUpdate.toLocaleTimeString()}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setVesselConfig(prev => ({
                                ...prev,
                                latitude: port.lat.toString(),
                                longitude: port.lon.toString(),
                                zoom: "11"
                              }));
                            }}
                            className="text-blue-600 hover:text-blue-700 mr-1"
                          >
                            📍
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setMonitoredPorts(monitoredPorts.filter(p => p.id !== port.id))}
                            className="text-red-600 hover:text-red-700"
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Monitored Vessels */}
                {monitoredVessels.length > 0 && (
                  <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-blue-600">🚢</span> Monitored Vessels ({monitoredVessels.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                      {monitoredVessels.map((vessel) => {
                        const currentVessel = vessels.find(v => v.mmsi === vessel.mmsi);
                        const hasDestinationChange = currentVessel && currentVessel.destination !== vessel.lastDestination;
                        const hasPositionChange = currentVessel && (
                          Math.abs(currentVessel.latitude! - vessel.lastPosition.lat) > 0.01 ||
                          Math.abs(currentVessel.longitude! - vessel.lastPosition.lon) > 0.01
                        );
                        
                        return (
                          <div key={vessel.id} className={`flex items-center justify-between p-2 rounded ${hasDestinationChange || hasPositionChange ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300' : 'bg-slate-50 dark:bg-slate-700/50'}`}>
                            <div className="flex-1">
                              <div className="text-sm font-medium">{vessel.name}</div>
                              <div className="text-xs text-slate-500">MMSI: {vessel.mmsi}</div>
                              {hasDestinationChange && (
                                <div className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                                  ⚠️ Destination changed: {vessel.lastDestination} → {currentVessel?.destination}
                                </div>
                              )}
                              {hasPositionChange && !hasDestinationChange && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                  📍 Position updated
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setMonitoredVessels(monitoredVessels.filter(v => v.id !== vessel.id))}
                              className="text-red-600 hover:text-red-700"
                            >
                              ✕
                            </Button>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Tools Tab */}
              <TabsContent value="tools" className="space-y-4">
                {vessels.length > 0 && (
                  <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-cyan-600">📊</span> Vessel Statistics
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Total Vessels:</span>
                        <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{vessels.length}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Underway:</span>
                        <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">{vessels.filter(v => v.status === 'Underway').length}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Stationary:</span>
                        <Badge className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">{vessels.filter(v => v.status !== 'Underway').length}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Tankers:</span>
                        <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{vessels.filter(v => v.type?.toLowerCase().includes('tanker')).length}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Container Ships:</span>
                        <Badge className="bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">{vessels.filter(v => v.type?.toLowerCase().includes('container')).length}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-lg">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-purple-600">🛠️</span> Export & Analysis Tools
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button
                      onClick={() => {
                        const csv = [
                          ['Name', 'MMSI', 'Type', 'Flag', 'Latitude', 'Longitude', 'Speed', 'Course', 'Destination', 'Status'].join(','),
                          ...vessels.map(v => [
                            v.name || '',
                            v.mmsi || '',
                            v.type || '',
                            v.flag || '',
                            v.latitude || '',
                            v.longitude || '',
                            v.speed || '',
                            v.course || '',
                            v.destination || '',
                            v.status || ''
                          ].join(','))
                        ].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `vessels_${new Date().toISOString().split('T')[0]}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                      disabled={vessels.length === 0}
                    >
                      📥 Export to CSV
                    </Button>
                    <Button
                      onClick={() => {
                        const json = JSON.stringify(vessels, null, 2);
                        const blob = new Blob([json], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `vessels_${new Date().toISOString().split('T')[0]}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
                      disabled={vessels.length === 0}
                    >
                      📄 Export to JSON
                    </Button>
                    <Button
                      onClick={() => {
                        const alerts: string[] = [];
                        monitoredVessels.forEach(v => {
                          const current = vessels.find(v2 => v2.mmsi === v.mmsi);
                          if (current) {
                            if (current.destination !== v.lastDestination) {
                              alerts.push(`🚢 ${v.name}: Destination changed from ${v.lastDestination} to ${current.destination}`);
                            }
                            const dist = Math.sqrt(
                              Math.pow(current.latitude! - v.lastPosition.lat, 2) +
                              Math.pow(current.longitude! - v.lastPosition.lon, 2)
                            ) * 111; // km
                            if (dist > 10) {
                              alerts.push(`📍 ${v.name}: Moved ${dist.toFixed(1)} km`);
                            }
                          }
                        });
                        if (alerts.length > 0) {
                          alert('Monitoring Alerts:\n\n' + alerts.join('\n'));
                        } else {
                          alert('No alerts - all monitored vessels are within normal parameters.');
                        }
                      }}
                      className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white"
                      disabled={monitoredVessels.length === 0}
                    >
                      🔔 Check Monitoring Alerts
                    </Button>
                    <Button
                      onClick={() => {
                        setMonitoringActive(prev => !prev);
                      }}
                      className={`w-full ${monitoringActive ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' : 'bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700'} text-white`}
                    >
                      {monitoringActive ? '⏸️ Stop Auto-Monitoring' : '▶️ Start Auto-Monitoring'}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Main Map Area */}
          <div className="lg:col-span-3">
            <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 shadow-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className="text-2xl">🗺️</span>
                    <span>Maritime Vessel Tracking</span>
                  </span>
                  <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                    {vessels.length} vessels
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-700">
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                  </div>
                )}
                <div style={{ height: 'calc(100vh - 250px)', minHeight: '600px', width: '100%', position: 'relative' }} className="rounded-lg overflow-hidden">
                  <MapContainer
                    center={mapCenter}
                    zoom={mapZoom}
                    style={{ height: '100%', width: '100%' }}
                    className="rounded-lg"
                  >
                    <MapViewUpdater center={mapCenter} zoom={mapZoom} />
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {/* Port/Harbor Marker */}
                    {detectedPort && (
                      <Marker
                        position={[detectedPort.lat, detectedPort.lon]}
                        icon={createPortIcon(detectedPort.name)}
                      >
                        <Popup>
                          <div className="text-center">
                            <div className="text-lg font-bold text-green-700">⚓ {detectedPort.name} Port</div>
                            <div className="text-xs text-slate-600 mt-1">
                              {detectedPort.lat.toFixed(4)}, {detectedPort.lon.toFixed(4)}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    )}
                    {vessels.map((vessel) => {
                      if (vessel.latitude == null || vessel.longitude == null || 
                          isNaN(vessel.latitude) || isNaN(vessel.longitude)) {
                        return null;
                      }
                      
                      const route = getVesselRoute(vessel);
                      const isStationary = (vessel.speed || 0) === 0 || vessel.status === 'Moored' || vessel.status === 'At Anchor';
                      
                      return (
                        <React.Fragment key={vessel.mmsi}>
                          {route && route.length > 1 && (
                            <Polyline
                              positions={route}
                              pathOptions={{
                                color: isStationary ? '#94a3b8' : (vessel.speed && vessel.speed > 15 ? '#3b82f6' : vessel.speed && vessel.speed > 5 ? '#60a5fa' : '#93c5fd'),
                                weight: isStationary ? 2 : (vessel.speed && vessel.speed > 15 ? 3 : 2),
                                opacity: isStationary ? 0.5 : 0.7,
                                dashArray: isStationary ? '10, 10' : (vessel.speed && vessel.speed > 15 ? '10, 5' : '5, 5'),
                                lineCap: 'round',
                                lineJoin: 'round'
                              }}
                            />
                          )}
                          <Marker
                            position={[vessel.latitude, vessel.longitude]}
                            icon={createVesselIcon(vessel)}
                            eventHandlers={{
                              click: () => {
                                setSelectedVessel(vessel);
                                setShowVesselDialog(true);
                              }
                            }}
                          >
                            <Popup maxWidth={400} className="vessel-popup">
                              <div className="p-0 w-full max-w-sm">
                                <div className="w-full h-40 bg-slate-200 dark:bg-slate-700 overflow-hidden relative rounded-t-lg">
                                  <VesselImage vessel={vessel} />
                                  <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                    {vessel.type}
                                  </div>
                                </div>
                                <div className="p-3 max-h-96 overflow-y-auto">
                                  <h3 className="font-bold text-base text-slate-800 dark:text-slate-200 mb-2">
                                    {vessel.name || 'Unknown Vessel'}
                                  </h3>
                                  <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                                    <div><strong>MMSI:</strong> {vessel.mmsi}</div>
                                    {vessel.imo && <div><strong>IMO:</strong> {vessel.imo}</div>}
                                    <div><strong>Type:</strong> {vessel.type}</div>
                                    <div><strong>Flag:</strong> {vessel.flag}</div>
                                    <div><strong>Speed:</strong> {vessel.speed?.toFixed(1) || 'N/A'} kn</div>
                                    <div><strong>Course:</strong> {vessel.course?.toFixed(1) || 'N/A'}°</div>
                                    <div><strong>Status:</strong> {vessel.status}</div>
                                  </div>
                                  <Button
                                    size="sm"
                                    className="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                                    onClick={() => {
                                      setSelectedVessel(vessel);
                                      setShowVesselDialog(true);
                                    }}
                                  >
                                    View Full Details
                                  </Button>
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Custom Markers */}
                    {customMarkers.map((marker) => (
                      <Marker
                        key={marker.id}
                        position={[marker.latitude, marker.longitude]}
                        icon={L.divIcon({
                          className: 'custom-marker',
                          html: `<div style="background: ${marker.color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
                          iconSize: [24, 24],
                          iconAnchor: [12, 12]
                        })}
                      >
                        <Popup>
                          <div className="p-2">
                            <div className="font-semibold">{marker.name}</div>
                            <div className="text-xs text-slate-500">{marker.type}</div>
                            {marker.description && <div className="text-xs mt-1">{marker.description}</div>}
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                  {loading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 rounded-lg">
                      <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-xl">
                        <div className="flex items-center gap-3">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <p className="text-slate-900 dark:text-white font-medium">Loading vessels...</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Vessel Details Dialog */}
      <Dialog open={showVesselDialog} onOpenChange={setShowVesselDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedVessel?.name || 'Vessel Details'}</DialogTitle>
          </DialogHeader>
          {selectedVessel && (
            <div className="space-y-4">
              <div className="w-full h-64 bg-slate-200 dark:bg-slate-700 overflow-hidden relative rounded-lg">
                <VesselImage vessel={selectedVessel} />
                <div className="absolute top-3 right-3 bg-black/70 text-white text-sm px-3 py-1 rounded">
                  {selectedVessel.type}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500 mb-1">MMSI</div>
                  <div className="font-semibold">{selectedVessel.mmsi}</div>
                </div>
                {selectedVessel.imo && (
                  <div>
                    <div className="text-xs text-slate-500 mb-1">IMO</div>
                    <div className="font-semibold">{selectedVessel.imo}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-slate-500 mb-1">Type</div>
                  <div className="font-semibold">{selectedVessel.type}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Flag</div>
                  <div className="font-semibold">{selectedVessel.flag}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Speed</div>
                  <div className="font-semibold">{selectedVessel.speed?.toFixed(1) || 'N/A'} knots</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Course</div>
                  <div className="font-semibold">{selectedVessel.course?.toFixed(1) || 'N/A'}°</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Status</div>
                  <Badge className={selectedVessel.status === 'Underway' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                    {selectedVessel.status}
                  </Badge>
                </div>
              </div>

              {(selectedVessel.length || selectedVessel.width || selectedVessel.draught) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 text-slate-700 dark:text-slate-300">Specifications</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedVessel.length && <div><strong>Length:</strong> {typeof selectedVessel.length === 'number' ? selectedVessel.length.toFixed(1) : selectedVessel.length} m</div>}
                    {selectedVessel.width && <div><strong>Width:</strong> {typeof selectedVessel.width === 'number' ? selectedVessel.width.toFixed(1) : selectedVessel.width} m</div>}
                    {selectedVessel.draught && <div><strong>Draught:</strong> {typeof selectedVessel.draught === 'number' ? selectedVessel.draught.toFixed(1) : selectedVessel.draught} m</div>}
                    {selectedVessel.gross_tonnage && <div><strong>Gross Tonnage:</strong> {typeof selectedVessel.gross_tonnage === 'number' ? selectedVessel.gross_tonnage.toLocaleString() : selectedVessel.gross_tonnage} GT</div>}
                    {selectedVessel.deadweight && <div><strong>Deadweight:</strong> {typeof selectedVessel.deadweight === 'number' ? selectedVessel.deadweight.toLocaleString() : selectedVessel.deadweight} DWT</div>}
                    {selectedVessel.year_built && <div><strong>Year Built:</strong> {selectedVessel.year_built}</div>}
                    {selectedVessel.builder && <div className="col-span-2"><strong>Builder:</strong> {selectedVessel.builder}</div>}
                    {selectedVessel.owner && <div className="col-span-2"><strong>Owner:</strong> {selectedVessel.owner}</div>}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3 text-slate-700 dark:text-slate-300">Route</h4>
                <div className="space-y-2 text-sm">
                  {selectedVessel.previous_port && <div><strong>Previous Port:</strong> {selectedVessel.previous_port}</div>}
                  {selectedVessel.current_port && <div><strong>Current Port:</strong> {selectedVessel.current_port}</div>}
                  {selectedVessel.destination && <div><strong>Destination:</strong> {selectedVessel.destination}</div>}
                  {selectedVessel.next_port && <div><strong>Next Port:</strong> {selectedVessel.next_port}</div>}
                  {selectedVessel.eta && <div><strong>ETA:</strong> {selectedVessel.eta}</div>}
                  {selectedVessel.distance_traveled && <div><strong>Distance Traveled:</strong> {selectedVessel.distance_traveled.toFixed(0)} nm</div>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        .vessel-marker {
          background: transparent !important;
          border: none !important;
        }
        .vessel-icon-container {
          position: relative;
          width: var(--icon-size, 40px);
          height: var(--icon-size, 40px);
          transform-origin: center;
        }
        .vessel-icon {
          position: relative;
          width: var(--icon-size, 40px);
          height: var(--icon-size, 40px);
          transform-origin: center;
        }
        .vessel-hull {
          position: absolute;
          top: 50%;
          left: 50%;
          width: calc(var(--icon-size, 40px) * 0.7);
          height: calc(var(--icon-size, 40px) * 0.25);
          background: linear-gradient(135deg, var(--vessel-color, #3b82f6), var(--vessel-color, #1d4ed8));
          border-radius: 4px 4px 8px 8px;
          transform: translate(-50%, -50%);
          box-shadow: 0 2px 8px rgba(0,0,0,0.6), 0 0 12px var(--vessel-color, #3b82f6);
          border: 2px solid rgba(255,255,255,0.8);
        }
        .vessel-superstructure {
          position: absolute;
          top: 30%;
          left: 50%;
          width: calc(var(--icon-size, 40px) * 0.4);
          height: calc(var(--icon-size, 40px) * 0.15);
          background: linear-gradient(135deg, var(--vessel-color, #3b82f6), var(--vessel-color, #1d4ed8));
          border-radius: 2px;
          transform: translate(-50%, -50%);
          box-shadow: 0 1px 4px rgba(0,0,0,0.5);
          border: 1px solid rgba(255,255,255,0.6);
        }
        .vessel-bow {
          position: absolute;
          top: 50%;
          left: 25%;
          width: 0;
          height: 0;
          border-left: calc(var(--icon-size, 40px) * 0.15) solid var(--vessel-color, #3b82f6);
          border-top: calc(var(--icon-size, 40px) * 0.08) solid transparent;
          border-bottom: calc(var(--icon-size, 40px) * 0.08) solid transparent;
          transform: translateY(-50%);
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        }
        .vessel-type-badge {
          position: absolute;
          top: 10%;
          right: 10%;
          width: calc(var(--icon-size, 40px) * 0.3);
          height: calc(var(--icon-size, 40px) * 0.3);
          background: rgba(0,0,0,0.7);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: calc(var(--icon-size, 40px) * 0.2);
          font-weight: bold;
          border: 1px solid rgba(255,255,255,0.5);
        }
        .vessel-arrow {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-left: calc(var(--icon-size, 40px) * 0.15) solid transparent;
          border-right: calc(var(--icon-size, 40px) * 0.15) solid transparent;
          border-top: calc(var(--icon-size, 40px) * 0.25) solid var(--vessel-color, #1d4ed8);
          transform: translate(-50%, -75%);
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
        }
        .vessel-wake {
          position: absolute;
          top: 50%;
          left: 50%;
          width: calc(var(--icon-size, 40px) * 0.8);
          height: calc(var(--icon-size, 40px) * 0.3);
          background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), transparent);
          transform: translate(-50%, 20%);
          border-radius: 50%;
          animation: wake 1.5s infinite;
        }
        .vessel-icon.moving {
          animation: vesselPulse 2s infinite;
        }
        .vessel-icon.moving .vessel-arrow {
          animation: arrowPulse 2s infinite;
        }
        .vessel-icon.moving .vessel-body {
          animation: bodyGlow 2s infinite;
        }
        @keyframes vesselPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.9; transform: scale(1.05); }
        }
        @keyframes arrowPulse {
          0%, 100% { opacity: 1; transform: translate(-50%, -75%) scale(1); }
          50% { opacity: 0.95; transform: translate(-50%, -75%) scale(1.15); }
        }
        @keyframes bodyGlow {
          0%, 100% { box-shadow: 0 2px 6px rgba(0,0,0,0.5), 0 0 8px rgba(59, 130, 246, 0.4); }
          50% { box-shadow: 0 2px 8px rgba(0,0,0,0.6), 0 0 12px rgba(59, 130, 246, 0.6); }
        }
        @keyframes wake {
          0% { opacity: 0.3; transform: translate(-50%, 20%) scale(0.8); }
          50% { opacity: 0.5; transform: translate(-50%, 25%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, 30%) scale(1.2); }
        }
        .vessel-icon.stationary-harbor .vessel-body {
          border: 3px solid var(--vessel-color, #3b82f6);
          box-shadow: 0 0 12px var(--vessel-color, #3b82f6), 0 2px 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(59, 130, 246, 0.3);
          animation: harborPulse 2s infinite;
        }
        .harbor-anchor {
          position: absolute;
          top: 50%;
          left: 50%;
          width: calc(var(--icon-size, 40px) * 0.5);
          height: calc(var(--icon-size, 40px) * 0.5);
          background: radial-gradient(circle, var(--vessel-color, #3b82f6), var(--vessel-color, #1d4ed8));
          border-radius: 50%;
          transform: translate(-50%, -50%);
          border: 3px solid white;
          box-shadow: 0 0 12px var(--vessel-color, #3b82f6), 0 0 24px rgba(59, 130, 246, 0.6), inset 0 0 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: calc(var(--icon-size, 40px) * 0.3);
        }
        .harbor-anchor::before {
          content: '⚓';
          color: white;
          text-shadow: 0 1px 3px rgba(0,0,0,0.8);
        }
        .port-marker {
          background: transparent !important;
          border: none !important;
        }
        .port-icon-container {
          position: relative;
          width: 60px;
          height: 60px;
        }
        .port-icon {
          position: relative;
          width: 60px;
          height: 60px;
        }
        .port-base {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #10b981, #059669);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          border: 4px solid white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4), 0 0 20px rgba(16, 185, 129, 0.6);
        }
        .port-flag {
          position: absolute;
          top: 20%;
          left: 50%;
          transform: translateX(-50%);
          font-size: 24px;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        }
        .port-label {
          position: absolute;
          bottom: -20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0,0,0,0.8);
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
          white-space: nowrap;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        .harbor-indicator {
          position: absolute;
          top: 50%;
          left: 50%;
          width: calc(var(--icon-size, 40px) * 0.4);
          height: calc(var(--icon-size, 40px) * 0.4);
          background: var(--vessel-color, #3b82f6);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          border: 3px solid white;
          box-shadow: 0 0 10px var(--vessel-color, #3b82f6), 0 0 20px rgba(59, 130, 246, 0.5);
          animation: harborIndicatorPulse 2s infinite;
        }
        @keyframes harborPulse {
          0%, 100% { box-shadow: 0 0 12px var(--vessel-color, #3b82f6), 0 2px 8px rgba(0,0,0,0.6), inset 0 0 8px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 16px var(--vessel-color, #3b82f6), 0 2px 10px rgba(0,0,0,0.7), inset 0 0 12px rgba(59, 130, 246, 0.5); }
        }
        @keyframes harborIndicatorPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.9; }
          50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
        }
        .vessel-popup .leaflet-popup-content-wrapper {
          border-radius: 8px;
          padding: 0;
        }
        .vessel-popup .leaflet-popup-content {
          margin: 0;
          width: auto !important;
        }
      `}</style>
    </div>
  );
};

// Form Components for OSINT Monitoring
function AddVesselMonitorForm({ onSubmit }: { onSubmit: (data: any) => void }) {
  const [formData, setFormData] = useState({
    identifier: '',
    name: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ identifier: '', name: '' });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">MMSI or IMO Number</label>
        <Input
          value={formData.identifier}
          onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
          placeholder="Enter MMSI (e.g., 123456789) or IMO"
          required
        />
        <p className="text-xs text-slate-500 mt-1">Enter the vessel's MMSI or IMO number to track</p>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Vessel Name (Optional)</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter vessel name for reference"
        />
      </div>
      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Add to Monitoring</Button>
    </form>
  );
}

function AddPortMonitorForm({ onSubmit }: { onSubmit: (data: any) => void }) {
  const [formData, setFormData] = useState({
    name: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ name: '' });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Port Name</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter port name (e.g., Karachi, Singapore, Rotterdam)"
          required
        />
        <p className="text-xs text-slate-500 mt-1">Enter the port name. Coordinates will be automatically resolved.</p>
      </div>
      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-xs text-blue-700 dark:text-blue-300">
        <strong>Supported ports:</strong> Karachi, Port Qasim, Singapore, Rotterdam, Mumbai, Kochi, Dubai, Shanghai, Hamburg, Los Angeles, and more.
      </div>
      <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">Add to Monitoring</Button>
    </form>
  );
}

function AddCoordinateForm({ onSubmit }: { onSubmit: (data: any) => void }) {
  const [formData, setFormData] = useState({
    name: '',
    latitude: '',
    longitude: '',
    description: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ name: '', latitude: '', longitude: '', description: '' });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Coordinate Name</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter coordinate name"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Latitude</label>
          <Input
            type="number"
            step="0.0001"
            value={formData.latitude}
            onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
            placeholder="e.g., 9.9312"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Longitude</label>
          <Input
            type="number"
            step="0.0001"
            value={formData.longitude}
            onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
            placeholder="e.g., 76.2673"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Description (Optional)</label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Additional notes"
        />
      </div>
      <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700">Add Coordinate</Button>
    </form>
  );
}

export default GEOINT_Maritime;
